import './PositionManagement.css';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { TrendingUp, RefreshCw, Plus, Search, AlertCircle, X, User, Clock, TrendingDown, Edit3, XCircle, Loader2 } from 'lucide-react';
import { positionAPI, fundsAPI } from '../../../../services';
import notificationService from '../../../../services/notificationService';
import { handlePermissionError } from '../../../../services/utils/permissionErrorHandler';
import ExportButton from '../../../../components/ExportButton';
import StatsBar from './components/StatsBar';
import PositionsTable from './components/PositionsTable';
import CloseTradeModal from './components/CloseTradeModal';
import AddTradeModal from './components/AddTradeModal';
import ModifyTradeModal from './components/ModifyTradeModal';
import {
  formatNumber,
  formatDate,
  formatDateTime,
  extractPositionsData,
  extractSummaryData,
  extractUserPositionsData,
  extractUserOrdersData,
  getSideLabel,
  getOrderStatusLabel,
  getOrderStatusClass,
  isOrderRemovable,
} from './components/utils';

const PositionManagement = () => {
  // ── Core state ──
  const [positions, setPositions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [processing, setProcessing] = useState(false);

  // ── Table state ──
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'netQuantity', direction: 'desc' });
  const [filterSide, setFilterSide] = useState('all');
  const [expandedSymbol, setExpandedSymbol] = useState(null);

  // ── User search & selection ──
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [users, setUsers] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPositions, setUserPositions] = useState(null);
  const [userOrders, setUserOrders] = useState(null);
  const [userLoading, setUserLoading] = useState(false);

  // ── Modals ──
  const [closeModal, setCloseModal] = useState({ isOpen: false, trade: null });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [modifyModal, setModifyModal] = useState({ isOpen: false, trade: null });

  // ── Auto-refresh ──
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced user search ──
  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(userSearchQuery), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSearchQuery]);

  // ── Fetch user data when selected ──
  useEffect(() => {
    if (selectedUser) {
      fetchUserData(selectedUser.id);
    } else {
      setUserPositions(null);
      setUserOrders(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  // ── Computed stats ──
  const stats = useMemo(() => {
    if (!positions) {
      return { totalInstruments: 0, longPositions: 0, shortPositions: 0, totalUsers: 0 };
    }
    const posValues = Object.values(positions);
    const allUserIds = new Set();
    posValues.forEach(p => {
      if (p.users) p.users.forEach(u => allUserIds.add(u));
    });
    return {
      totalInstruments: posValues.length,
      longPositions: posValues.filter(p => p.netQuantity > 0).length,
      shortPositions: posValues.filter(p => p.netQuantity < 0).length,
      totalUsers: allUserIds.size,
    };
  }, [positions]);

  // ═══════════════════ DATA FETCHING ═══════════════════

  const fetchData = useCallback(async () => {
    try {
      setError('');
      const [positionsData, summaryData] = await Promise.all([
        positionAPI.getAggregatedPositions(),
        positionAPI.getPositionsSummary(),
      ]);
      setPositions(extractPositionsData(positionsData));
      setLastUpdated(new Date());
      extractSummaryData(summaryData);
    } catch (err) {
      console.error('Failed to fetch position data:', err);
      setError(err.message || 'Failed to load position data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async (search) => {
    if (!search.trim()) {
      setUsers(null);
      return;
    }
    try {
      setUsersLoading(true);
      const result = await fundsAPI.getUsers({ search: search.trim(), limit: 10, page: 1 });
      if (result.success && result.data) {
        const userList = result.data.users || [];
        setUsers(userList.map(u => ({ ...u, id: u.id || u.accountId || u._id })));
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const fetchUserData = useCallback(async (userId) => {
    setUserLoading(true);
    try {
      const [posResult, ordResult] = await Promise.all([
        positionAPI.getUserPositions(userId),
        positionAPI.getUserOrders(userId),
      ]);
      setUserPositions(extractUserPositionsData(posResult));
      setUserOrders(extractUserOrdersData(ordResult));
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setUserPositions([]);
      setUserOrders([]);
    } finally {
      setUserLoading(false);
    }
  }, []);

  const fetchTableUserOrders = useCallback(async (userId) => {
    // For the instrument table expansion
    try {
      const result = await positionAPI.getUserOrders(userId);
      const orders = extractUserOrdersData(result);
      return orders;
    } catch {
      return [];
    }
  }, []);

  // ═══════════════════ HANDLERS ═══════════════════

  const handleRefresh = () => {
    setLoading(true);
    setExpandedSymbol(null);
    fetchData();
    if (selectedUser) fetchUserData(selectedUser.id);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handleToggleExpand = async (symbol) => {
    if (expandedSymbol === symbol) {
      setExpandedSymbol(null);
    } else {
      setExpandedSymbol(symbol);
    }
  };

  const handleUserSelect = (user) => {
    const normalized = { ...user, id: user.id || user.accountId || user._id };
    setSelectedUser(normalized);
    setUsers(null);
    setUserSearchQuery('');
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setUserPositions(null);
    setUserOrders(null);
  };

  const handleOpenCloseModal = (trade, userId) => {
    setCloseModal({ isOpen: true, trade: { ...trade, userId: userId || trade.userId } });
  };
  const handleCloseModal = () => setCloseModal({ isOpen: false, trade: null });

  const handleConfirmClose = async (closeData) => {
    try {
      setProcessing(true);
      const result = await positionAPI.closeTrade(
        closeData.userId,
        closeData.tradeId,
        { closePercentage: closeData.closePercentage, closeQuantity: closeData.closeQuantity }
      );
      if (result?.data?.success === false || result?.success === false) {
        throw new Error(result?.data?.error || result?.data?.info || 'Failed to close trade');
      }
      fetchData();
      if (selectedUser) fetchUserData(selectedUser.id);
      notificationService.success('Position closed successfully!');
    } catch (err) {
      const isPermErr = handlePermissionError(err);
      if (!isPermErr) {
        setError(err.message || 'Failed to close trade');
        notificationService.error(err.message || 'Failed to close trade');
      }
    } finally {
      setProcessing(false);
      handleCloseModal();
    }
  };

  const handleOpenModifyModal = (trade, userId) => {
    setModifyModal({ isOpen: true, trade: { ...trade, userId: userId || trade.userId } });
  };
  const handleCloseModifyModal = () => setModifyModal({ isOpen: false, trade: null });

  const handleConfirmModify = async (modifyData) => {
    try {
      setProcessing(true);
      const result = await positionAPI.modifyTrade(
        modifyData.userId,
        modifyData.tradeId,
        { takeProfit: modifyData.takeProfit, stopLoss: modifyData.stopLoss, quantity: modifyData.quantity, entryPrice: modifyData.entryPrice, timestamp: modifyData.timestamp }
      );
      if (result?.data?.success === false || result?.success === false) {
        throw new Error(result?.data?.error || result?.data?.info || 'Failed to modify trade');
      }
      fetchData();
      if (selectedUser) fetchUserData(selectedUser.id);
      notificationService.success('Trade modified successfully!');
    } catch (err) {
      const isPermErr = handlePermissionError(err);
      if (!isPermErr) {
        setError(err.message || 'Failed to modify trade');
        notificationService.error(err.message || 'Failed to modify trade');
      }
    } finally {
      setProcessing(false);
      handleCloseModifyModal();
    }
  };

  const handleRemoveOrder = async (order) => {
    try {
      setProcessing(true);
      const result = await positionAPI.removeOrder(order.userId || selectedUser?.id, order.orderId, 'Removed by admin');
      if (result?.data?.success === false || result?.success === false) {
        throw new Error(result?.data?.error || result?.data?.info || 'Failed to remove order');
      }
      fetchData();
      if (selectedUser) fetchUserData(selectedUser.id);
      notificationService.success('Order removed successfully!');
    } catch (err) {
      const isPermErr = handlePermissionError(err);
      if (!isPermErr) {
        setError(err.message || 'Failed to remove order');
        notificationService.error(err.message || 'Failed to remove order');
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmAdd = async (tradeData) => {
    try {
      setProcessing(true);
      const result = await positionAPI.addTrade(tradeData);
      if (result?.data?.success === false || result?.success === false) {
        throw new Error(result?.data?.error || result?.data?.info || 'Failed to add trade');
      }
      fetchData();
      if (selectedUser) fetchUserData(selectedUser.id);
      notificationService.success('Trade added successfully!');
    } catch (err) {
      const isPermErr = handlePermissionError(err);
      if (!isPermErr) {
        setError(err.message || 'Failed to add trade');
        notificationService.error(err.message || 'Failed to add trade');
      }
      throw err;
    } finally {
      setProcessing(false);
      setAddModalOpen(false);
    }
  };

  // ═══════════════════ FILTERED & SORTED DATA ═══════════════════

  const filteredAndSorted = useMemo(() => {
    if (!positions) return [];

    let filtered = Object.entries(positions).filter(([symbol, data]) => {
      const matchesSearch = symbol.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterSide === 'all' ||
        (filterSide === 'long' && data.netQuantity > 0) ||
        (filterSide === 'short' && data.netQuantity < 0);
      return matchesSearch && matchesFilter;
    });

    filtered.sort((a, b) => {
      const aValue = a[1][sortConfig.key];
      const bValue = b[1][sortConfig.key];
      const modifier = sortConfig.direction === 'asc' ? 1 : -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * modifier;
      }
      return String(aValue || '').localeCompare(String(bValue || '')) * modifier;
    });

    return filtered;
  }, [positions, searchTerm, filterSide, sortConfig]);

  // ═══════════════════ EXPORT ═══════════════════

  const exportData = useMemo(() => {
    if (!positions) return [];
    return Object.entries(positions).map(([symbol, data]) => ({
      symbol,
      netQuantity: data.netQuantity ?? '',
      side: data.netQuantity > 0 ? 'LONG' : data.netQuantity < 0 ? 'SHORT' : 'FLAT',
      longQty: data.totalBuyQuantity ?? '',
      shortQty: data.totalSellQuantity ?? '',
      userCount: data.userCount ?? '',
      avgBuy: data.averageBuyPrice ?? '',
      avgSell: data.averageSellPrice ?? '',
    }));
  }, [positions]);

  const exportColumns = [
    { key: 'symbol', header: 'Symbol' },
    { key: 'netQuantity', header: 'Net Qty', format: (v) => formatNumber(v, 2), align: 'right' },
    { key: 'side', header: 'Side' },
    { key: 'longQty', header: 'Long Qty', format: (v) => formatNumber(v, 2), align: 'right' },
    { key: 'shortQty', header: 'Short Qty', format: (v) => formatNumber(v, 2), align: 'right' },
    { key: 'userCount', header: 'Users', align: 'right' },
    { key: 'avgBuy', header: 'Avg Buy', format: (v) => v ? formatNumber(v, 5) : '---', align: 'right' },
    { key: 'avgSell', header: 'Avg Sell', format: (v) => v ? formatNumber(v, 5) : '---', align: 'right' },
  ];

  // ═══════════════════ RENDER ═══════════════════

  if (loading && !positions) {
    return (
      <div className="pm">
        <div className="pm-loading">
          <div className="pm-spinner" />
          <p>Loading positions...</p>
        </div>
      </div>
    );
  }

  if (error && !positions) {
    return (
      <div className="pm">
        <div className="pm-error">
          <h4>Error Loading Data</h4>
          <p>{error}</p>
          <button className="pm-btn pm-btn--primary" onClick={handleRefresh}>
            <RefreshCw size={14} /> Try Again
          </button>
        </div>
      </div>
    );
  }

  const userId = selectedUser?.id;
  const userTrades = userPositions || [];
  const userOrds = userOrders || [];

  return (
    <div className="pm">
      {/* ════ HEADER ════ */}
      <div className="pm-header">
        <div className="pm-header-left">
          <h1><TrendingUp /> Position Management</h1>
          <p>Net positions across all instruments. Click any instrument to see user breakdown.</p>
        </div>
        <div className="pm-header-right">
          {lastUpdated && (
            <span className="pm-updated">Updated: {formatDate(lastUpdated)}</span>
          )}
          <ExportButton
            data={exportData}
            columns={exportColumns}
            filename="position-management"
            title="Position Management"
            subtitle={'Exported on ' + formatDate(new Date())}
          />
          <button className="pm-btn pm-btn--ghost" onClick={handleRefresh}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* ════ STATS ════ */}
      <StatsBar stats={stats} />

      {/* ════ USER SEARCH BAR ════ */}
      <div className="pm-user-bar">
        <div className="pm-user-bar-search">
          <Search className="pm-user-bar-icon" />
          <input
            type="text"
            placeholder="Search user by name, email, or ID..."
            value={userSearchQuery}
            onChange={(e) => setUserSearchQuery(e.target.value)}
            className="pm-user-bar-input"
          />
          {usersLoading && <Loader2 size={14} className="pm-spin pm-user-bar-loader" />}
        </div>

        {/* Dropdown results */}
        {users && users.length > 0 && (
          <div className="pm-user-dropdown">
            {users.map(user => (
              <div key={user.id} className="pm-user-dropdown-item" onClick={() => handleUserSelect(user)}>
                <div className="pm-user-dropdown-avatar">
                  {(user.username || user.email || user.id).charAt(0).toUpperCase()}
                </div>
                <div className="pm-user-dropdown-info">
                  <span className="pm-user-dropdown-name">{user.username || user.email || user.id}</span>
                  <span className="pm-user-dropdown-sub">{user.id}</span>
                </div>
                <span className="pm-user-dropdown-type">{user.type}</span>
              </div>
            ))}
          </div>
        )}

        {selectedUser && (
          <>
            <div className="pm-user-bar-selected">
              <User size={13} />
              <span className="pm-user-bar-name">{selectedUser.username || selectedUser.email || selectedUser.id}</span>
              <button className="pm-user-bar-clear" onClick={handleClearUser}>
                <X size={12} />
              </button>
            </div>
            <button
              className="pm-btn pm-btn--primary"
              onClick={() => setAddModalOpen(true)}
              disabled={processing}
              style={{ fontSize: '0.76rem' }}
            >
              <Plus size={13} /> Add Trade
            </button>
          </>
        )}
      </div>

      {/* ════ USER DETAIL PANEL ════ */}
      {selectedUser && (
        <div className="pm-user-panel">
          <div className="pm-user-panel-head">
            <div className="pm-user-panel-avatar">
              <User size={18} />
            </div>
            <div>
              <div className="pm-user-panel-name">{selectedUser.username || selectedUser.email || 'Unknown'}</div>
              <div className="pm-user-panel-id">{selectedUser.id}</div>
            </div>
            <div className="pm-user-panel-badges">
              {selectedUser.isVerified
                ? <span className="pm-badge pm-badge--success">Verified</span>
                : <span className="pm-badge pm-badge--warn">Unverified</span>
              }
              {selectedUser.isBlocked
                ? <span className="pm-badge pm-badge--danger">Blocked</span>
                : <span className="pm-badge pm-badge--info">Active</span>
              }
            </div>
            <button className="pm-user-panel-close" onClick={handleClearUser}><X size={14} /></button>
          </div>

          {userLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
              <div className="pm-spinner" style={{ width: 24, height: 24 }} />
            </div>
          ) : (
            <div className="pm-user-panel-body">
              {/* ── OPEN TRADES ── */}
              <div className="pm-user-section">
                <h4 className="pm-user-section-title">
                  <TrendingUp size={13} /> Open Trades
                  <span className="pm-user-section-count">{userTrades.length}</span>
                </h4>
                {userTrades.length === 0 ? (
                  <div className="pm-user-empty">No open trades</div>
                ) : (
                  <div className="pm-user-trades-list">
                    {userTrades.map(trade => (
                      <div className="pm-user-trade-row" key={trade.tradeId}>
                        <div className="pm-user-trade-left">
                          <span className={'pm-trade-tag pm-trade-tag--' + trade.side}>
                            {getSideLabel(trade.side)}
                          </span>
                          <span className="pm-user-trade-symbol">{trade.symbol}</span>
                        </div>
                        <div className="pm-user-trade-mid">
                          <span className="pm-trade-detail">{formatNumber(trade.quantity, 2)} @ {formatNumber(trade.entryPrice, 5)}</span>
                          {trade.leverage && (
                            <span className="pm-user-trade-lev">1:{trade.leverage}x</span>
                          )}
                          {trade.baseCurrency && (
                            <span className="pm-user-currency">{trade.baseCurrency}</span>
                          )}
                        </div>
                        <div className="pm-user-trade-right">
                          {trade.takeProfit != null && (
                            <span className="pm-user-trade-tpsl">TP {formatNumber(trade.takeProfit, 5)}</span>
                          )}
                          {trade.stopLoss != null && (
                            <span className="pm-user-trade-tpsl">SL {formatNumber(trade.stopLoss, 5)}</span>
                          )}
                        </div>
                        <div className="pm-user-trade-actions">
                          <button
                            className="pm-act"
                            onClick={() => handleOpenModifyModal(trade, userId)}
                            disabled={processing}
                          >
                            <Edit3 /> Modify
                          </button>
                          <button
                            className="pm-act pm-act--danger"
                            onClick={() => handleOpenCloseModal(trade, userId)}
                            disabled={processing}
                          >
                            <TrendingDown /> Close
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── PENDING ORDERS ── */}
              <div className="pm-user-section">
                <h4 className="pm-user-section-title">
                  <Clock size={13} /> Pending Orders
                  <span className="pm-user-section-count">{userOrds.length}</span>
                </h4>
                {userOrds.length === 0 ? (
                  <div className="pm-user-empty">No pending orders</div>
                ) : (
                  <div className="pm-user-trades-list">
                    {userOrds.map(order => (
                      <div className="pm-user-trade-row pm-user-trade-row--order" key={order.orderId}>
                        <div className="pm-user-trade-left">
                          <span className={'pm-trade-tag pm-trade-tag--' + order.side}>
                            {getSideLabel(order.side)}
                          </span>
                          <span className="pm-user-trade-symbol">{order.symbol}</span>
                        </div>
                        <div className="pm-user-trade-mid">
                          <span className="pm-trade-detail">{formatNumber(order.quantity, 2)} @ {formatNumber(order.price, 5)}</span>
                          <span className={'pm-user-order-status pm-user-order-status--' + getOrderStatusClass(order.status)}>
                            {getOrderStatusLabel(order.status)}
                          </span>
                        </div>
                        <div className="pm-user-trade-actions">
                          {isOrderRemovable(order.status) && (
                            <button
                              className="pm-act pm-act--danger"
                              onClick={() => handleRemoveOrder({ ...order, userId })}
                              disabled={processing}
                            >
                              <XCircle /> Cancel
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════ TOOLBAR ════ */}
      <div className="pm-toolbar">
        <div className="pm-search">
          <Search />
          <input
            type="text"
            placeholder="Search instruments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="pm-filters">
          <button
            className={'pm-filter ' + (filterSide === 'all' ? 'active' : '')}
            onClick={() => setFilterSide('all')}
          >
            All ({Object.keys(positions || {}).length})
          </button>
          <button
            className={'pm-filter ' + (filterSide === 'long' ? 'active' : '')}
            onClick={() => setFilterSide('long')}
          >
            Long ({stats.longPositions})
          </button>
          <button
            className={'pm-filter ' + (filterSide === 'short' ? 'active' : '')}
            onClick={() => setFilterSide('short')}
          >
            Short ({stats.shortPositions})
          </button>
        </div>
      </div>

      {/* ════ ERROR ALERT ════ */}
      {error && positions && (
        <div className="pm-error-alert">
          <AlertCircle size={15} /> {error}
        </div>
      )}

      {/* ════ TABLE ════ */}
      <PositionsTable
        positions={positions}
        filteredAndSorted={filteredAndSorted}
        sortConfig={sortConfig}
        onSort={handleSort}
        expandedSymbol={expandedSymbol}
        onToggleExpand={handleToggleExpand}
        processing={processing}
        onOpenCloseModal={(trade) => handleOpenCloseModal(trade, trade.userId)}
        onOpenModifyModal={(trade) => handleOpenModifyModal(trade, trade.userId)}
        onRemoveOrder={handleRemoveOrder}
        userOrders={{}}
        formatNumber={formatNumber}
      />

      {/* ════ MODALS ════ */}
      <CloseTradeModal
        isOpen={closeModal.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmClose}
        trade={closeModal.trade}
        processing={processing}
        formatNumber={formatNumber}
      />

      <AddTradeModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onConfirm={handleConfirmAdd}
        selectedUser={selectedUser}
        processing={processing}
      />

      <ModifyTradeModal
        isOpen={modifyModal.isOpen}
        onClose={handleCloseModifyModal}
        onConfirm={handleConfirmModify}
        trade={modifyModal.trade}
        processing={processing}
        formatNumber={formatNumber}
      />
    </div>
  );
};

export default PositionManagement;
